import { Injectable } from '@angular/core';
import { AppConfig } from '../app.config';
/* declare var ezcrypto: any; */

@Injectable()
export class SessionService{
    constructor(private config: AppConfig){}

    /**
        * removeItem: removes a key from localStorage and its sibling expiracy key
        * params: key <string>     : localStorage key to remove
        * returns: <boolean> : telling if operation succeeded
     */
    public removeItem(key: string): boolean {
        try {
            localStorage.removeItem(key);
            localStorage.removeItem(key + '_expiresIn');
        } catch(e) {
            console.log('removeItem: Error removing key ['+ key + '] from localStorage: ' + JSON.stringify(e) );
            return false;
        }
        return true;
    }

    /**
     * getItem: retrieves a key from localStorage previously set with setStorage().
     * params: key <string> : localStorage key
     * returns: <string> : value of localStorage key
     *     null : in case of expired key or failure
     */
    public getItem(key: string) {

        let now: number = Date.now();  //epoch time, lets deal only with integer
        let rawValue: string | null = localStorage.getItem(key);

        if (rawValue === undefined || rawValue === null) {
            this.removeItem(key);
            return null;
        }

        // set expiration for storage
        let expiresValue: string | null = localStorage.getItem(key + '_expiresIn');
        let expiresIn: number = Number(expiresValue);
        if (expiresValue === undefined || expiresValue === null || Number.isNaN(expiresIn))
            expiresIn = 0;

        if (expiresIn < now) {// Expired
            this.removeItem(key);
            return null;
        } else {
            try {
                let value: string = this.decrypt(rawValue);
                return JSON.parse(value);
            } catch(e) {
                console.log('getItem: Error reading key ['+ key + '] from localStorage: ', e);
                this.removeItem(key);
                return null;
            }
        }
    }

    /**
     * setItem: writes a key into localStorage setting a expire time
     * param: key <string>     : localStorage key
     * param: value <string>   : localStorage value
     * param: expires <number> : number of seconds from now to expire the key
     * returns: <boolean> : telling if operation succeeded
     */
    public setItem(key: string, value: string, expires?: number) {
        // console.log(value)
        if (expires === undefined || expires === null) {
            expires = (24*60*60);  // default: seconds for 1 day
        } else {
            expires = Math.abs(expires); //make sure it's positive
        }

        let now: number = Date.now();  //millisecs since epoch time, lets deal only with integer
        let schedule: number = now + expires * 500;

        try {
            localStorage.setItem(key, this.encrypt(value));
            localStorage.setItem(key + '_expiresIn', String(schedule));
        } catch(e) {
            console.log('setItem: Error setting key ['+ key + '] in localStorage: ', e);
            return false;
        }
        return true;
    }

    public exist(key: string): boolean {
        let now: number = Date.now();  //epoch time, lets deal only with integer
        // set expiration for storage
        let expiresIn: number = Number(localStorage.getItem(key + '_expiresIn'));
        if (expiresIn === undefined || expiresIn === null)
            expiresIn = 0;

        if (expiresIn < now) {// Expired
            this.removeItem(key);
            return false;
        } else {
            try {
                return localStorage.getItem(key) !== undefined && localStorage.getItem(key) !== null;
            } catch(e) {
                console.log('getItem: Error reading key ['+ key + '] from localStorage: ', e);
                return false;
            }
        }
    }

    private encrypt(value: any): string {
        value = JSON.stringify(value);
        let encryptedData = btoa(encodeURIComponent(value).replace(/%([0-9A-F]{2})/gi, function (match, p1) {
            return String.fromCharCode(parseInt(p1, 16));
        }));
        return JSON.stringify({'value': encryptedData});
    }

    private decrypt(value: any): string {
        if (value === undefined || value === null) {
            throw new TypeError('Storage value is empty');
        }

        let parsedValue: any = JSON.parse(value);
        if (!parsedValue || typeof parsedValue.value !== 'string') {
            return JSON.stringify(parsedValue);
        }

        try {
            return decodeURIComponent(
                atob(parsedValue.value).split('').map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join('')
            );
        } catch (e) {
            if (parsedValue.value && (parsedValue.value.trim().charAt(0) === '{' || parsedValue.value.trim().charAt(0) === '[')) {
                return parsedValue.value;
            }

            throw e;
        }
    }



}
